import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, AlertTriangle, Shield } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { BankStatusCard } from './analytics/BankStatusCard'
import { KpiCard } from './analytics/KpiCard'

import {
  getSystemHealth,
  getBankPerformance,
  getApiTrend,
  getSecurityAnomalies,
} from '@/src/lib/api/admin-analytics'
import type {
  SystemHealth,
  BankPerformanceRow,
  ApiTrendPoint,
  SecurityAnomalies,
} from '@/src/types/admin/analytics'

const POLL_MS = 5 * 60 * 1000 // 5 minutes

const TIME_RANGES = [
  { label: '2 ชม.', hours: 2 },
  { label: 'สัปดาห์', hours: 168 },
  { label: 'เดือน', hours: 720 },
  { label: '3 เดือน', hours: 2160 },
  { label: 'ทั้งหมด', hours: 0 },
]

function formatHour(isoStr: string, hours: number): string {
  if (hours > 0 && hours <= 48) {
    return new Date(isoStr).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
  }
  const d = new Date(isoStr)
  return d.toLocaleDateString('th-TH', { day: '2-digit', month: 'short' })
}

export default function AdminTechnicalDashboard() {
  const adminUserData = (() => {
    try { return JSON.parse(localStorage.getItem('userData') || 'null') } catch { return null }
  })()
  const canViewSecurity = adminUserData?.permissions?.includes('security:configure') ?? false

  const [health, setHealth] = useState<SystemHealth | null>(null)
  const [banks, setBanks] = useState<BankPerformanceRow[]>([])
  const [trend, setTrend] = useState<ApiTrendPoint[]>([])
  const [security, setSecurity] = useState<SecurityAnomalies | null>(null)
  const [loading, setLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [selectedHours, setSelectedHours] = useState(720)

  const fetchAll = useCallback(async (hours = selectedHours) => {
    setLoading(true)
    try {
      const calls: Promise<void>[] = [
        getSystemHealth(hours).then(setHealth),
        getBankPerformance(hours).then(setBanks),
        getApiTrend(hours).then(setTrend),
      ]
      if (canViewSecurity) {
        calls.push(getSecurityAnomalies().then(setSecurity))
      }
      await Promise.all(calls)
      setLastUpdated(new Date())
    } finally {
      setLoading(false)
    }
  }, [canViewSecurity, selectedHours])

  useEffect(() => {
    fetchAll()
    const id = setInterval(fetchAll, POLL_MS)
    return () => clearInterval(id)
  }, [fetchAll])

  const handleRangeChange = (hours: number) => {
    setSelectedHours(hours)
    fetchAll(hours)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">ประสิทธิภาพทางเทคนิค</h1>
          <p className="text-muted-foreground">ประสิทธิภาพ API, Cache และความปลอดภัย</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Time range toggle */}
          <div className="flex items-center rounded-md border bg-background p-0.5 gap-0.5">
            {TIME_RANGES.map((r) => (
              <button
                key={r.hours}
                onClick={() => handleRangeChange(r.hours)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  selectedHours === r.hours
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">
              อัพเดตล่าสุด {lastUpdated.toLocaleTimeString('th-TH')}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={() => fetchAll()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            รีเฟรช
          </Button>
        </div>
      </div>

      {/* System Health KPIs */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="อัตราสำเร็จ API"
          value={health ? `${health.success_rate_pct.toFixed(1)}%` : '—'}
          loading={loading && !health}
        />
        <KpiCard
          title="เวลาตอบสนอง P95"
          value={health ? `${health.p95_response_ms.toFixed(0)} ms` : '—'}
          loading={loading && !health}
          invertTrend
        />
        <KpiCard
          title="Cache Hit Rate"
          value={health ? `${health.cache.hit_rate_pct.toFixed(1)}%` : '—'}
          loading={loading && !health}
        />
        <KpiCard
          title="Error Rate (24h)"
          value={health ? `${health.error_rate_pct.toFixed(2)}%` : '—'}
          loading={loading && !health}
          invertTrend
        />
      </div>

      {/* Bank Status Cards */}
      <div>
        <h2 className="text-lg font-semibold mb-3">สถานะธนาคาร</h2>
        {loading && banks.length === 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {banks.map((b) => (
              <BankStatusCard
                key={b.bank_code}
                bankCode={b.bank_code}
                successRate={b.success_rate_pct}
                avgResponseMs={b.avg_response_ms}
                p95ResponseMs={b.p95_response_ms}
                status={b.status}
              />
            ))}
          </div>
        )}
      </div>

      {/* API Trend Chart + Bank Performance Table */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              API Requests ({TIME_RANGES.find(r => r.hours === selectedHours)?.label ?? 'เดือน'})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && trend.length === 0 ? (
              <Skeleton className="h-[260px] w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart
                  data={trend.map((d) => ({
                    hour: formatHour(d.hour, selectedHours),
                    requests: d.total_requests,
                    p95: d.p95_response_ms,
                  }))}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                >
                  <defs>
                    <linearGradient id="colorReq" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={36} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="requests"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#colorReq)"
                    name="Requests"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Bank Performance</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && banks.length === 0 ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ธนาคาร</TableHead>
                    <TableHead className="text-right">สำเร็จ %</TableHead>
                    <TableHead className="text-right">avg ms</TableHead>
                    <TableHead className="text-right">P95 ms</TableHead>
                    <TableHead>สถานะ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {banks.map((b) => (
                    <TableRow key={b.bank_code}>
                      <TableCell className="font-medium">{b.bank_code}</TableCell>
                      <TableCell className="text-right">{b.success_rate_pct.toFixed(1)}%</TableCell>
                      <TableCell className="text-right">{b.avg_response_ms.toFixed(0)}</TableCell>
                      <TableCell className="text-right">{b.p95_response_ms.toFixed(0)}</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            b.status === 'healthy'
                              ? 'text-green-600 border-green-200'
                              : b.status === 'degraded'
                                ? 'text-amber-600 border-amber-200'
                                : 'text-red-600 border-red-200'
                          }
                        >
                          {b.status === 'healthy' ? 'ปกติ' : b.status === 'degraded' ? 'ช้า' : 'ขัดข้อง'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Security Section — only visible with security:configure */}
      {canViewSecurity ? (
        <Card>
          <CardHeader className="flex flex-row items-center gap-2">
            <Shield className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">Security & Anomalies</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading && !security ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-lg border p-3">
                    <p className="text-sm text-muted-foreground">Flagged Activities</p>
                    <p className="text-2xl font-bold">{security?.flagged_activities.count ?? 0}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-sm text-muted-foreground">Suspicious IPs</p>
                    <p className="text-2xl font-bold">
                      {security?.failed_auth.suspicious_ips.length ?? 0}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-sm text-muted-foreground">Unusual IPs</p>
                    <p className="text-2xl font-bold">
                      {security?.unusual_ips.items.length ?? 0}
                    </p>
                  </div>
                </div>

                {security && security.flagged_activities.items.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ประเภท</TableHead>
                        <TableHead>IP</TableHead>
                        <TableHead className="text-right">Risk Score</TableHead>
                        <TableHead>เวลา</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {security.flagged_activities.items.slice(0, 10).map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>{a.action_type}</TableCell>
                          <TableCell className="font-mono text-xs">{a.ip_address}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant={a.risk_score >= 90 ? 'destructive' : 'secondary'}>
                              {a.risk_score}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(a.at).toLocaleString('th-TH')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex items-center gap-3 py-6">
            <AlertTriangle className="h-5 w-5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              คุณไม่มีสิทธิ์ดูข้อมูล Security (ต้องการ <code>security:configure</code>)
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
